// Zero-backend auth: a Supabase client built from public env vars. Mirrors the
// donate.ts pattern — a single ENABLED flag gates the whole feature, so the app
// runs fine with no credentials (the login UI falls back to "coming soon").
//
// To turn auth on: create a Supabase project, enable the Google + Discord
// providers and email magic-link, then set VITE_SUPABASE_URL and
// VITE_SUPABASE_ANON_KEY (see DEV-AUTH.md). The anon key is public by design.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const URL = import.meta.env.VITE_SUPABASE_URL || "";
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// Auth entry points render real sign-in only once both values are configured.
export const AUTH_ENABLED = !!(URL && ANON_KEY && !URL.includes("REPLACE_ME"));

// The sign-in window a framed game opens (shared/auth-frame.ts): it signs in
// only to hand the session over, so it keeps it in memory and never refreshes
// it (two copies refreshing one token would get the session revoked).
const SIGN_IN_WINDOW = typeof window !== "undefined" && window.location.pathname === "/auth/popup";

// Single shared client (null when auth is disabled, so callers must guard).
export const supabase: SupabaseClient | null = AUTH_ENABLED
  ? createClient(URL, ANON_KEY, {
      auth: {
        persistSession: !SIGN_IN_WINDOW,
        autoRefreshToken: !SIGN_IN_WINDOW,
        // Consume the OAuth/magic-link tokens Supabase appends to the URL on
        // redirect back to the app, then clean them from the address bar.
        detectSessionInUrl: true,
      },
    })
  : null;

// The session Supabase keeps on this device, read at once (no await): the app's
// first frame can then be drawn signed in, instead of signed out for a moment
// until getSession() answers and the page jumps. supabase-js's own key,
// "sb-<project ref>-auth-token". Null when there is none or it is unreadable;
// getSession() still has the last word (an expired token it cannot refresh
// signs out a moment later, as before).
export function storedSession(): any | null {
  // (never in the sign-in window: whatever this browser keeps for the site is
  // not what it signs in to hand over, see shared/auth-frame.ts)
  if (!AUTH_ENABLED || typeof window === "undefined" || SIGN_IN_WINDOW) return null;
  try {
    const ref = new globalThis.URL(URL).hostname.split(".")[0];
    const raw = window.localStorage.getItem(`sb-${ref}-auth-token`);
    const s = raw ? JSON.parse(raw) : null;
    return s && s.user && s.access_token ? s : null;
  } catch { return null; }
}
